import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { RecurrenceEditor, RecurrenceEditorComponent } from '@syncfusion/ej2-angular-schedule';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ScheduledBooking } from 'src/app/simulator-booking/models/scheduled-booking';
import { ScheduledBookingTypeEnum, ScheduledBookingTypeEnumLabel } from 'src/app/shared/models/scheduled-booking-type.enum';
import { BaseModalComponent } from 'src/app/core/base-modal.componant';
import { Simulator } from 'src/app/simulator-booking/models/simulators';
import { SessionService } from 'src/app/shared/services/session.service';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { ScheduledBookingService } from 'src/app/simulator-booking/services/scheduled-booking.service';
import { LeagueService } from 'src/app/simulator-booking/services/league.service';
import { LeagueIterationService } from 'src/app/simulator-booking/services/league-iteration.service';
import moment from 'moment';
import { LeagueIteration } from 'src/app/simulator-booking/models/league-iteration';
import { League } from 'src/app/simulator-booking/models/league';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'edit-scheduled-booking-modal',
  templateUrl: 'edit-scheduled-booking-modal.component.html',
  styleUrls: ['./edit-scheduled-booking-modal.component.scss'],
  standalone: false
})
export class EditScheduledBookingModalComponent extends BaseModalComponent implements OnInit {
  @ViewChild('recurrenceObj') recurrenceObj!: RecurrenceEditorComponent;

  id: string;
  recurrenceForm: FormGroup;
  recurrenceRule: string = '';
  selectedStartDate: Date;
  selectedEndDate: Date;
  selectedSimulators: any[] = [];
  scheudledBookingTypes: any[] = ScheduledBookingTypeEnumLabel;
  simulators: Simulator[] = [];
  leagues: League[] = [];
  leagueIterations: LeagueIteration[] = [];
  scheduledBooking: ScheduledBooking;
  enabledStates = {
    startDate: true,
    endDate: true,
    startTime: true,
    endTime: true,
    name: true,
    price: true
  };
  constructor(
    private dialogRef: MatDialogRef<EditScheduledBookingModalComponent>,
    private formBuilder: FormBuilder,
    private scheduledBookingService: ScheduledBookingService,
    private sessionService: SessionService,
    private leagueIterationService: LeagueIterationService,
    private leagueService: LeagueService,
    private dialog: MatDialog
  ) {
    super();

    this.recurrenceForm = this.formBuilder.group({
      startDateOnly: ['', Validators.required],
      endDateOnly: ['', Validators.required],
      startTimeOnly: ['', Validators.required],
      endTimeOnly: ['', Validators.required],
      name: ['', Validators.required],
      price: ['', [Validators.required, Validators.min(0)]],
      simulatorIds: [[], Validators.required],
      type: [''],
      paymentLink: [''],
      recurrenceRule: [''],
      referenceId: [null],
      iterationId: [''],
      leagueId: [''],
      customerEmail: [''],
      sendPaymentLink: [false]
    });

    this.selectedSimulators = [];
  }

  ngOnInit(): void {
    this.getScheduledBooking();
  }

  getScheduledBooking(): void {
    this.isLoading = true;
    this.scheduledBookingService.getById(this.id).subscribe({
      next: (scheuldedBookingResult: any) => {
        this.recurrenceForm.patchValue(scheuldedBookingResult);
        this.selectedStartDate = scheuldedBookingResult.startTime;
        this.selectedEndDate = scheuldedBookingResult.endTime;
        this.recurrenceRule = scheuldedBookingResult.recurrenceRule;

        scheuldedBookingResult.scheuldedBookingSimulators.forEach((scheduledBookingSimulator: any) => {
          this.selectedSimulators.push(scheduledBookingSimulator.simulatorId);
        });

        const hasCustomerEmail = !!(scheuldedBookingResult.customerEmail);
        this.recurrenceForm.patchValue({
          simulatorIds: this.selectedSimulators,
          customerEmail: scheuldedBookingResult.customerEmail || '',
          sendPaymentLink: hasCustomerEmail
        });

        // Set validators if customerEmail exists
        const emailControl = this.recurrenceForm.get('customerEmail');
        if (hasCustomerEmail) {
          emailControl?.setValidators([Validators.required, Validators.email]);
        } else {
          emailControl?.clearValidators();
        }
        emailControl?.updateValueAndValidity();

        this.recurrenceForm.get('startTimeOnly').setValue(moment(scheuldedBookingResult.startTime).format('h:mm A'));
        this.recurrenceForm.get('endTimeOnly').setValue(moment(scheuldedBookingResult.endTime).format('h:mm A'));

        if (scheuldedBookingResult.type === ScheduledBookingTypeEnum.League && scheuldedBookingResult.referenceId !== null) {
          this.enabledStates.name = false;
          this.enabledStates.price = false;
          this.enabledStates.startDate = false;
          this.enabledStates.endDate = false;
          this.enabledStates.startTime = false;
          this.enabledStates.endTime = false;

          this.leagueService.getAllByOrganization({
            organizationId: this.sessionService.getUser().organizationId
          }).subscribe({
            next: (leaguesResult: any) => {
              this.leagues = leaguesResult.results || [];
            },
            error: (error: any) => {
              console.error('Error loading leagues:', error);
              this.isLoading = false;
            },
            complete: () => {
              let selectedIterationId;
              this.leagueIterationService.getById(this.recurrenceForm.get('referenceId').value).subscribe({
                next: (leagueIterationResult: any) => {
                  selectedIterationId = leagueIterationResult.id;
                  this.recurrenceForm.patchValue({
                    leagueId: leagueIterationResult.leagueId
                  });
                  this.recurrenceForm.get('name').setValue(this.leagues.find((league: any) => league.id === leagueIterationResult.leagueId)?.name);
                  this.enabledStates.name = false;
                },
                error: (error: any) => {
                  console.error('Error loading iteration:', error);
                },
                complete: () => {
                  let leagueId = this.recurrenceForm.get('leagueId').value;
                  this.leagueIterationService.getAllByLeagueId({
                    leagueId: leagueId,
                    text: '',
                    page: 1,
                    pageSize: 1000
                  }).subscribe({
                    next: (leagueIterationsResult: any) => {
                      this.leagueIterations = leagueIterationsResult.results || [];
                      this.recurrenceForm.patchValue({
                        iterationId: selectedIterationId
                      });
                      this.isLoading = false;
                    },
                    error: (error: any) => {
                      console.error('Error loading iterations:', error);
                      this.isLoading = false;
                    }
                  });
                }
              });
            }
          });
        } else {
          this.isLoading = false;
        }
      },
      error: (error: any) => {
        this.alert(error);
        console.error(error);
        this.isLoading = false;
      }
    });
  }

  onStartDateChange(date: Date): void {
    this.selectedStartDate = date;
    this.recurrenceForm.patchValue({
      startDate: date
    });
  }

  onEndDateChange(date: Date): void {
    this.selectedEndDate = date;
    this.recurrenceForm.patchValue({
      endDate: date
    });

    let rule = this.recurrenceObj.getRecurrenceRule();
    if (rule.includes('UNTIL=')) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      this.recurrenceRule = `${rule.split('UNTIL=')[0]};UNTIL=${`${year}${month}${day}T${hours}${minutes}${seconds}Z`}`;
    } else if (rule.includes('COUNT=')) {
      const startDate = moment(this.selectedStartDate);
      const endDate = moment(date);
      const count = endDate.diff(startDate, 'weeks');
      this.recurrenceRule = `${rule.split('COUNT=')[0]};COUNT=${count + 1}`;
    }
  }

  onRecurrenceChange(args: any): void {
    this.recurrenceRule = this.recurrenceObj.getRecurrenceRule();
    this.recurrenceForm.patchValue({
      recurrenceRule: this.recurrenceRule
    });
  }

  onSendPaymentLinkChange(args: any): void {
    const emailControl = this.recurrenceForm.get('customerEmail');
    if (args.checked) {
      emailControl?.setValidators([Validators.required, Validators.email]);
    } else {
      emailControl?.clearValidators();
      emailControl?.setValue('');
    }
    emailControl?.updateValueAndValidity();
  }

  onSelectedAllSimulators(args: any): void {
    this.selectedSimulators = args.value;
    this.recurrenceForm.patchValue({
      simulatorIds: args.value.map((s: any) => s && s.id ? s.id : s)
    });
  }

  onSimulatorChange(args: any): void {
    try {
      if (args && args.value && Array.isArray(args.value)) {
        this.selectedSimulators = args.value;
        const simulatorIds = args.value.map((s: any) => s && s.id ? s.id : s);
        this.recurrenceForm.patchValue({
          simulatorIds: simulatorIds
        });
      } else if (args && args.value === null) {
        // Handle deselection
        this.selectedSimulators = [];
        this.recurrenceForm.patchValue({
          simulatorIds: []
        });
      }
    } catch (error) {
      console.error('Error in onSimulatorChange:', error);
      // Reset to empty array on error
      this.selectedSimulators = [];
      this.recurrenceForm.patchValue({
        simulatorIds: []
      });
    }
  }

  onScheduledBookingTypeChange(args: any): void {
    this.recurrenceForm.patchValue({
      type: args.itemData.type
    });

    if (args.itemData.type === ScheduledBookingTypeEnum.League) {
      this.getLeagues();
    } else if (args.previousItemData.type === ScheduledBookingTypeEnum.League) {
      this.leagues = [];
      this.leagueIterations = [];
      this.recurrenceForm.patchValue({
        referenceId: null,
        iterationId: null,
        leagueId: null,
        name: null,
        price: null,
        recurrenceRule: '',
      });

      this.recurrenceObj.setRecurrenceRule('');
      this.enabledStates.name = true;
      this.enabledStates.price = true;
      this.enabledStates.startDate = true;
      this.enabledStates.endDate = true;
      this.enabledStates.startTime = true;
      this.enabledStates.endTime = true;
    }
  }

  getLeagues(): void {
    this.leagueService.getAllByOrganization({
      organizationId: this.sessionService.getUser().organizationId
    }).subscribe({
      next: (result: any) => {
        this.leagues = result.results || [];
      },
      error: (error: any) => {
        console.error('Error loading leagues:', error);
      }
    });
  }

  getLeaguesIterations(args: any): void {
    let filter = {
      leagueId: args.itemData.id,
      text: '',
      page: 1,
      pageSize: 1000
    };
    this.leagueIterationService.getAllByLeagueId(filter).subscribe({
      next: (result: any) => {
        this.leagueIterations = result.results || [];
        this.recurrenceForm.get('name').setValue(args.itemData.name);
        this.enabledStates.name = false;
      },
      error: (error: any) => {
        console.error('Error loading iterations:', error);
      }
    });
  }


  onIterationChange(args: any): void {
    this.recurrenceForm.patchValue({
      iterationId: args.itemData.id
    });

    this.selectedStartDate = moment.utc(args.itemData.startDate).tz(this.sessionService.getUser().timeZone).toDate();
    this.selectedEndDate = moment.utc(args.itemData.endDate).tz(this.sessionService.getUser().timeZone).toDate();
    this.recurrenceForm.patchValue({
      startDateOnly: this.selectedStartDate,
      endDateOnly: this.selectedEndDate,
    });

    this.recurrenceForm.get('price').setValue(args.itemData.entryFee);

    const startTimeOnly = moment.utc(args.itemData.startDate).tz(this.sessionService.getUser().timeZone);
    const endTimeOnly = moment.utc(args.itemData.endDate).tz(this.sessionService.getUser().timeZone);

    this.recurrenceForm.patchValue({
      startTimeOnly: startTimeOnly.format('h:mm A'),
      endTimeOnly: endTimeOnly.format('h:mm A')
    });

    this.enabledStates.startDate = false;
    this.enabledStates.endDate = false;
    this.enabledStates.startTime = false;
    this.enabledStates.endTime = false;
    this.enabledStates.price = false;
  }

  onSave(): void {
    if (this.recurrenceForm.valid) {
      const startDate = this.selectedStartDate;
      const endDate = this.selectedEndDate;

      const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      const daysDifference = Math.ceil((endDateOnly.getTime() - startDateOnly.getTime()) / (1000 * 60 * 60 * 24));

      if (daysDifference > 1 && !this.recurrenceRule) {
        const dialogRef = this.dialog.open(AlertModalComponent, {
          width: '40vw',
        });

        dialogRef.componentInstance.title = 'Confirm Multi-Day Booking';
        dialogRef.componentInstance.body = `<h3 class="mat-h3">This booking spans ${daysDifference} days.</h3>
        <p>Are you sure you want to create a booking that extends past a single day?</p>
        <p><strong>Recommendation:</strong> Consider using a recurrence rule instead to create multiple single-day bookings.</p>`;
        dialogRef.componentInstance.submitText = 'Yes, Create Booking';
        dialogRef.componentInstance.otherText = 'Cancel';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        dialogRef.afterClosed().subscribe((response) => {
          if (response === 'Yes, Create Booking') {
            this.proceedWithSave();
          }
        });
        return;
      }

      this.proceedWithSave();
    } else {
      this.setError(null, this.recurrenceForm);
    }
  }

  private proceedWithSave(): void {
    this.isLoading = true;
    this.recurrenceForm.removeControl('iterationId');
    this.recurrenceForm.removeControl('leagueId');
    const formData = this.recurrenceForm.value;

    const startDate = this.selectedStartDate;
    const endDate = this.selectedEndDate;

    formData.startDateOnly = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    formData.endDateOnly = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

    formData.price = parseFloat(formData.price) || 0;

    if (formData.recurrenceRule && formData.recurrenceRule.endsWith(';')) {
      formData.recurrenceRule = formData.recurrenceRule.slice(0, -1);
    }

    formData.startTimeOnly = moment(this.recurrenceForm.get('startTimeOnly').value, ['h:mm A']).format('HH:mm:ss');
    formData.endTimeOnly = moment(this.recurrenceForm.get('endTimeOnly').value, ['h:mm A']).format('HH:mm:ss');

    // Only include customerEmail if sendPaymentLink is enabled
    if (!formData.sendPaymentLink) {
      delete formData.customerEmail;
    }

    formData.organizationId = this.sessionService.getUser().organizationId;
    formData.lastEditedBy = this.sessionService.getUserId();
    formData.timeZone = this.sessionService.getUser().timeZone;
    formData.id = this.id;

    this.scheduledBookingService.update(this.id, formData).subscribe({
      next: (result) => {
        this.isLoading = false;
        if (result !== null || result !== undefined) {
          this.dialogRef.close({
            action: 'save',
            data: formData
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.alert(error);
        console.error(error);
      }
    });
  }

  onClose(): void {
    this.dialogRef.close({
      action: 'cancel'
    });
  }


  onDeleteSeries(): void {
    let dialogRef = this.dialog.open(AlertModalComponent, {
      width: '35vw',
    });
    dialogRef.componentInstance.title = 'Delete Series';
    dialogRef.componentInstance.body = 'Are you sure you want to delete this series? This action cannot be undone.';
    dialogRef.componentInstance.submitText = 'Yes';
    dialogRef.componentInstance.otherText = 'No';
    dialogRef.componentInstance.isOtherTextDisabled = false;
    dialogRef.afterClosed().subscribe((response) => {
      if (response === 'Yes') {
        let data = {
          id: this.id,
          lastEditedBy: this.sessionService.getUserId(),
          timezone: this.sessionService.getUser().timeZone,
          organizationId: this.sessionService.getUser().organizationId,
        };
        this.scheduledBookingService.delete(data).subscribe({
          next: (result) => {
            this.dialogRef.close();
            this.isLoading = false;
          },
          error: (error) => {
            this.alert(error);
            console.error(error);
            this.isLoading = false;
          }
        });
      }
    });
  }

  onDelete(): void {
    let dialogRef = this.dialog.open(AlertModalComponent, {
      width: '35vw',
    });
    dialogRef.componentInstance.title = 'Delete Scheduled Booking';
    dialogRef.componentInstance.body = `Are you sure you want to delete this scheduled booking? This action cannot be undone. 
    <br /><br /> If this was a recurring booking, all future bookings will also be deleted but previous bookings will remain in the scheduler.`

    dialogRef.componentInstance.submitText = 'Yes';
    dialogRef.componentInstance.otherText = 'No';
    dialogRef.componentInstance.isOtherTextDisabled = false;
    dialogRef.afterClosed().subscribe((response) => {
      if (response === 'Yes') {
        let data = {
          id: this.id,
          lastEditedBy: this.sessionService.getUserId(),
          timezone: this.sessionService.getUser().timeZone,
          organizationId: this.sessionService.getUser().organizationId,
        };
        this.scheduledBookingService.delete(data).subscribe({
          next: (result) => {
            this.dialogRef.close('save');
            this.isLoading = false;
          },
          error: (error) => {
            this.alert(error);
            console.error(error);
            this.isLoading = false;
          }
        });
      }
    });
  }

  onGenerateRule(): void {
    if (this.recurrenceObj) {
      this.recurrenceRule = this.recurrenceObj.getRecurrenceRule();
      this.recurrenceForm.patchValue({
        recurrenceRule: this.recurrenceRule
      });
    }
  }

  onPayNow(): void {
    let dialogRef = this.dialog.open(AlertModalComponent, {
      width: '35vw',
    });

    dialogRef.componentInstance.title = 'Pay Now';
    dialogRef.componentInstance.body = 'Would you like to pay in person or online?';
    dialogRef.componentInstance.submitText = 'In Person';
    dialogRef.componentInstance.otherText = 'Online';
    dialogRef.componentInstance.isOtherTextDisabled = false;
    dialogRef.afterClosed().subscribe((response) => {
      if (response === 'In Person') {
        window.open(this.scheduledBooking.paymentLink, '_blank');
      } else {
        window.open(this.scheduledBooking.paymentLink, '_blank');
      }
    });
  }

  alert(error: any) {
    let dialogRef = this.dialog.open(AlertModalComponent, {
      width: '35vw',
    });

    let errorMessage = '<h3 class="mat-h3">You have following errors:</h3><ul> ';

    if (error.status === 400) {
      error.error.forEach((error: any) => {
        errorMessage += `<li>${error.errorMessage}</li>`;
      });
    } else {
      errorMessage += `<li>There was an error updating the scheduled booking, Please try again or contact support if the problem persists</li>`;
    }

    dialogRef.componentInstance.title = 'Update Error';
    dialogRef.componentInstance.body = errorMessage + '</ul>';
    dialogRef.componentInstance.submitText = 'OK';
  }
}
